// FieldArray of store request line items. Description is a creatable product select (MW products or free text).
import React, { Fragment } from "react";
import { Field, useForm } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { OnChange } from "react-final-form-listeners";
import { Button, Col, FormGroup, Label, Row, Table } from "reactstrap";
import { InputField, ReactSelect, required } from "modules/4_final_form/web";
import _ from "lodash";

const blankLine = () => ({ description: "", quantity: 1 });

const DescriptionField = ({ field, disabled, productOptions }) => {
  const form = useForm();

  return (
    <Fragment>
      <Field
        name={`${field}.description`}
        component={ReactSelect}
        options={productOptions}
        validate={required}
        disabled={disabled}
        creatable
        virtualList
        isClearable
        placeholder="Select or type a product"
      />
      <OnChange name={`${field}.description`}>
        {(value) => {
          const opt = _.find(productOptions, { value });
          form.change(`${field}.stockcode`, opt?.stockcode || (value ? "MISC" : null));
        }}
      </OnChange>
    </Fragment>
  );
};

const LineRow = ({ field, index, remove, disabled, onAdd, isLast, productOptions }) => {
  const handleQtyKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    } else if (e.key === "Tab" && !e.shiftKey && isLast) {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <tr>
      <td>
        <DescriptionField field={field} disabled={disabled} productOptions={productOptions} />
      </td>
      <td style={{ width: 120 }}>
        <Field
          name={`${field}.quantity`}
          component={InputField}
          type="number"
          validate={required}
          disabled={disabled}
          onKeyDown={handleQtyKeyDown}
        />
      </td>
      <td style={{ width: 70 }}>
        {!disabled && (
          <Button color="danger" size="sm" type="button" onClick={() => remove(index)}>
            ×
          </Button>
        )}
      </td>
    </tr>
  );
};

// ponytail: stacked Cols when web form is in outlookMode
const StackedLineRow = ({ field, index, remove, disabled, onAdd, isLast, productOptions }) => {
  const handleQtyKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    } else if (e.key === "Tab" && !e.shiftKey && isLast) {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div className="border rounded p-2 mb-2">
      <Row className="align-items-end">
        <Col xs={12} className="mb-2">
          <Label className="mb-1">Description</Label>
          <DescriptionField field={field} disabled={disabled} productOptions={productOptions} />
        </Col>
        <Col xs={8} sm={6}>
          <Label className="mb-1">Qty</Label>
          <Field
            name={`${field}.quantity`}
            component={InputField}
            type="number"
            validate={required}
            disabled={disabled}
            onKeyDown={handleQtyKeyDown}
          />
        </Col>
        <Col xs={4} sm={6} className="text-right">
          {!disabled && (
            <Button color="danger" size="sm" type="button" onClick={() => remove(index)}>
              ×
            </Button>
          )}
        </Col>
      </Row>
    </div>
  );
};

const LineItemsFields = ({ disabled, stacked, productOptions = [] }) => (
  <Fragment>
    <FormGroup>
      <Label>
        <b>Line Items</b>
      </Label>
      <FieldArray name="line_items_attributes">
        {({ fields }) => {
          const addLine = () => {
            const nextIndex = fields.length;
            fields.push(blankLine());
            // ponytail: focus after paint; upgrade to ref map if names change
            setTimeout(() => {
              document.getElementById(`line_items_attributes[${nextIndex}].description`)?.focus();
            }, 0);
          };

          let lastVisibleIndex = -1;
          const rows = fields.value || [];
          for (let i = rows.length - 1; i >= 0; i--) {
            if (!rows[i]?._destroy) {
              lastVisibleIndex = i;
              break;
            }
          }

          const remove = (i) => {
            const row = fields.value[i];
            if (row?.id) {
              fields.update(i, { ...row, _destroy: true });
            } else {
              fields.remove(i);
            }
          };

          const rowProps = (field, index) => ({
            key: field,
            field,
            index,
            disabled,
            productOptions,
            isLast: index === lastVisibleIndex,
            onAdd: addLine,
            remove,
          });

          return (
            <Fragment>
              {stacked ? (
                fields.map((field, index) => {
                  if (fields.value[index]?._destroy) return null;
                  return <StackedLineRow {...rowProps(field, index)} />;
                })
              ) : (
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field, index) => {
                      if (fields.value[index]?._destroy) return null;
                      return <LineRow {...rowProps(field, index)} />;
                    })}
                  </tbody>
                </Table>
              )}
              {!disabled && (
                <Button color="secondary" size="sm" type="button" onClick={addLine}>
                  Add Line
                </Button>
              )}
            </Fragment>
          );
        }}
      </FieldArray>
    </FormGroup>
  </Fragment>
);

export default LineItemsFields;
